import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Landmark, Users, Globe2, Stamp, ArrowLeftRight, GraduationCap, ShieldCheck, ExternalLink, Crown, Radio } from "lucide-react";
import KushFlag from "./KushFlag";
import DiasporaChapters from "./DiasporaChapters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();

const VISA_FREE_PRIVILEGES = [
  { icon: Stamp, text: "Visa-free entry for every holder of the One Passport" },
  { icon: ArrowLeftRight, text: "Free trade and business rights across its borders" },
  { icon: GraduationCap, text: "Youth mobility, study and exchange corridors" },
];

const VOA_PRIVILEGES = [
  { icon: Stamp, text: "Visa on arrival / e-Visa for One Passport holders" },
  { icon: ArrowLeftRight, text: "Trade access while full ratification completes" },
  { icon: ShieldCheck, text: "Full visa-free access upon treaty ratification" },
];

export function NationFlag({ nation, className }) {
  if (nation.founding) {
    return <KushFlag className={`${className} rounded-sm border border-white/10`} />;
  }
  if (nation.diaspora) {
    return (
      <span className={`${className} inline-flex items-center justify-center rounded-sm border border-[#4ADE80]/50 bg-[#12211A]`} data-testid="diaspora-emblem">
        <Globe2 className="w-2/3 h-2/3 text-[#4ADE80]" />
      </span>
    );
  }
  return (
    <img
      src={`https://flagcdn.com/w160/${nation.code.toLowerCase()}.png`}
      alt={`${nation.name} flag`}
      loading="lazy"
      className={`${className} object-cover rounded-sm border border-white/10`}
    />
  );
}

function Fact({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-[#00A651]/12 bg-[#0D1013] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#00A651]">
        <Icon className="w-3.5 h-3.5" />
        <p className="font-official text-[9px] tracking-[0.25em] uppercase text-[#8A99AD]">{label}</p>
      </div>
      <p className="font-display font-bold text-sm text-[#F7F5F0] mt-2">{value}</p>
    </div>
  );
}

function DispatchFeed({ nation }) {
  const [dispatches, setDispatches] = useState(null);

  useEffect(() => {
    if (!nation) return;
    setDispatches(null);
    axios
      .get(`${API}/nations/${nation.code}/dispatches`)
      .then((r) => setDispatches(r.data))
      .catch(() => setDispatches([]));
  }, [nation]);

  return (
    <div className="mt-6" data-testid="country-dispatches">
      <div className="flex items-center gap-2.5">
        <Radio className="w-3.5 h-3.5 text-[#00A651]" />
        <p className="font-official text-[10px] tracking-[0.3em] uppercase text-[#00A651]">
          Union Dispatches & Council Announcements
        </p>
      </div>
      <div className="mt-4 space-y-3">
        {dispatches === null &&
          [0, 1, 2].map((i) => (
            <div key={i} className="animate-pulse rounded-xl border border-[#00A651]/10 bg-[#0D1013] h-20" data-testid={`dispatch-skeleton-${i}`} />
          ))}
        {dispatches !== null && dispatches.length === 0 && (
          <p className="font-official text-[10px] tracking-[0.2em] uppercase text-[#8A99AD]" data-testid="dispatch-empty">
            No dispatches yet — the council convenes soon.
          </p>
        )}
        {dispatches !== null &&
          dispatches.map((d, i) => (
            <div key={d.id} className="rounded-xl border border-[#00A651]/12 bg-[#0D1013] p-4" data-testid={`dispatch-item-${i}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="font-official text-[9px] tracking-[0.25em] uppercase text-[#00A651]">{d.tag}</span>
                <span className="font-official text-[9px] tracking-[0.15em] text-[#8A99AD]">{formatDate(d.published_at)}</span>
              </div>
              <h4 className="font-display font-bold text-sm text-[#F7F5F0] mt-2 leading-snug">{d.title}</h4>
              <p className="text-xs text-[#8A99AD] mt-1.5 leading-relaxed">{d.excerpt}</p>
            </div>
          ))}
      </div>
    </div>
  );
}

export default function CountryDialog({ nation, onClose }) {
  return (
    <AnimatePresence>
      {nation && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          data-testid="country-dialog"
        >
          <div className="absolute inset-0 bg-[#0A0C0E]/85 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 48, scale: 0.95, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 24, scale: 0.97, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl border border-[#00A651]/30 bg-[#121519] p-6 sm:p-8 shadow-[0_40px_90px_rgba(0,0,0,0.7)]"
            data-testid="country-dialog-panel"
          >
            <button
              onClick={onClose}
              data-testid="country-dialog-close"
              className="absolute top-5 right-5 w-9 h-9 rounded-full border border-[#00A651]/30 flex items-center justify-center text-[#8A99AD] hover:text-[#00A651] hover:border-[#00A651] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-5">
              <NationFlag nation={nation} className="w-16 h-11 shrink-0" />
              <div>
                <div className="flex items-center gap-2">
                  {nation.founding && <Crown className="w-4 h-4 text-[#00A651]" />}
                  <p className="font-official text-[10px] tracking-[0.3em] uppercase text-[#00A651]">
                    {nation.founding ? "Founding State of the Union" : nation.diaspora ? "The Sixth Region of the Union" : `${nation.region} Africa`}
                  </p>
                </div>
                <h3 className="font-display font-extrabold tracking-tight text-2xl text-[#F7F5F0] mt-1.5" data-testid="country-dialog-name">
                  {nation.name}
                </h3>
              </div>
            </div>

            <div className="mt-5">
              <span
                className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-official text-[10px] tracking-[0.2em] uppercase border ${
                  nation.visaFree
                    ? "border-[#4ADE80]/50 text-[#4ADE80] bg-[#4ADE80]/10"
                    : "border-[#EF3340]/50 text-[#EF3340] bg-[#EF3340]/10"
                }`}
                data-testid="country-dialog-visa-status"
              >
                {nation.visaFree ? "Visa-Free — One Passport" : "Visa on Arrival / E-Visa"}
              </span>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Fact icon={Landmark} label="Capital" value={nation.capital} testid="country-fact-capital" />
              <Fact icon={Globe2} label="Region" value={nation.diaspora ? "Global — Sixth Region" : `${nation.region} Africa`} testid="country-fact-region" />
              <Fact icon={MapPin} label="Regional Council" value={nation.bloc} testid="country-fact-council" />
              <Fact icon={Stamp} label={nation.founding ? "Founded" : nation.diaspora ? "Proclaimed" : "Ratified"} value={String(nation.year)} testid="country-fact-year" />
            </div>

            {nation.founding && (
              <div className="mt-6 rounded-xl border border-[#00A651]/30 bg-[#0D1013] p-5" data-testid="kush-detail-block">
                <div className="grid grid-cols-2 gap-3">
                  <Fact icon={Users} label="Population" value={nation.population} testid="kush-fact-population" />
                  <Fact icon={Users} label="Citizenship Applicants" value={nation.applicants} testid="kush-fact-applicants" />
                </div>
                <p className="text-sm text-[#8A99AD] leading-relaxed mt-4">
                  <span className="text-[#00A651] font-official text-[10px] tracking-[0.25em] uppercase block mb-1.5">Location</span>
                  {nation.location} — the Bir Tawil territory, homeland of the Kingdom of Kush and birthplace of the One Passport.
                </p>
                <div className="mt-4 rounded-lg overflow-hidden border border-[#00A651]/25" data-testid="kush-territory-map">
                  <iframe
                    title="Bir Tawil — Kingdom of Kush territory"
                    src="https://www.openstreetmap.org/export/embed.html?bbox=31.70%2C21.00%2C35.70%2C22.60&layer=mapnik&marker=21.8245%2C33.7370"
                    className="w-full h-56 grayscale invert-[0.92] hue-rotate-180 contrast-[0.9]"
                  />
                </div>
                <p className="font-official text-[9px] tracking-[0.25em] uppercase text-[#8A99AD] mt-2">
                  Bir Tawil Territory — Kingdom of Kush
                </p>
                <a
                  href={nation.applyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="kush-apply-link"
                  className="mt-5 inline-flex items-center gap-2.5 rounded-full bg-[#00A651] text-[#0A0C0E] font-display font-bold text-xs px-6 py-3 hover:bg-[#2FC36A] hover:shadow-[0_0_24px_rgba(0,166,81,0.4)] transition-all duration-300"
                >
                  Apply for Citizenship — kingdomofkush.org
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            )}

            {nation.diaspora && (
              <div className="mt-6 rounded-xl border border-[#4ADE80]/30 bg-[#0D1013] p-5" data-testid="diaspora-detail-block">
                <div className="grid grid-cols-2 gap-3">
                  <Fact icon={Users} label="People of African Descent" value="350,000,000+" testid="diaspora-fact-population" />
                  <Fact icon={MapPin} label="Location" value="Worldwide" testid="diaspora-fact-location" />
                </div>
                <p className="text-sm text-[#8A99AD] leading-relaxed mt-4">
                  <span className="text-[#00A651] font-official text-[10px] tracking-[0.25em] uppercase block mb-1.5">
                    Charter — Chapter IV
                  </span>
                  The African Diaspora is an integral part of the global African family — the Sixth Region — whose
                  contribution to Africa's development, renaissance, and future is indispensable. The Kingdom of Kush
                  stands as the physical Pan-African home of return.
                </p>
                <DiasporaChapters />
              </div>
            )}

            <DispatchFeed nation={nation} />

            <div className="mt-6">
              <p className="font-official text-[10px] tracking-[0.3em] uppercase text-[#00A651]">Travel Privileges</p>
              <div className="mt-4 space-y-3">
                {(nation.visaFree ? VISA_FREE_PRIVILEGES : VOA_PRIVILEGES).map((p) => (
                  <div key={p.text} className="flex items-center gap-3.5">
                    <span className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 ${nation.visaFree ? "border-[#4ADE80]/40 text-[#4ADE80]" : "border-[#EF3340]/40 text-[#EF3340]"}`}>
                      <p.icon className="w-3.5 h-3.5" />
                    </span>
                    <p className="text-sm text-[#8A99AD]">{p.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
